import axios from "axios";
import { useState, useCallback } from "react";
import { hostname } from "../../../utils";
import {
  KeywordMonitor,
  CreateMonitorRequest,
  UpdateMonitorRequest,
  ListMonitorsParams,
} from "../types";

export const useMonitors = (
  getToken: () => Promise<string>,
  companyId: string
) => {
  const [monitors, setMonitors] = useState<KeywordMonitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const listMonitors = useCallback(
    async (params?: ListMonitorsParams) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/api/v0/monitors`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { companyId, ...params },
          }
        );
        setMonitors(response.data.monitors);
        setTotal(response.data.count);
        return response.data;
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.error || err.message || "Failed to load monitors";
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getToken, companyId]
  );

  const getMonitor = useCallback(
    async (monitorId: string) => {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/api/v0/monitors/${monitorId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data.monitor;
    },
    [getToken, companyId]
  );

  const createMonitor = useCallback(
    async (data: CreateMonitorRequest) => {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/api/v0/monitors`,
        { ...data, companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.monitor;
    },
    [getToken, companyId]
  );

  const updateMonitor = useCallback(
    async (monitorId: string, data: UpdateMonitorRequest) => {
      const token = await getToken();
      const response = await axios.put(
        `${hostname}/api/v0/monitors/${monitorId}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data.monitor;
    },
    [getToken, companyId]
  );

  const deleteMonitor = useCallback(
    async (monitorId: string) => {
      const token = await getToken();
      await axios.delete(
        `${hostname}/api/v0/monitors/${monitorId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
    },
    [getToken, companyId]
  );

  const pauseMonitor = useCallback(
    async (monitorId: string) => {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/api/v0/monitors/${monitorId}/pause`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data.monitor;
    },
    [getToken, companyId]
  );

  const resumeMonitor = useCallback(
    async (monitorId: string) => {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/api/v0/monitors/${monitorId}/resume`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data.monitor;
    },
    [getToken, companyId]
  );

  const triggerRun = useCallback(
    async (monitorId: string) => {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/api/v0/monitors/${monitorId}/run`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data;
    },
    [getToken, companyId]
  );

  return {
    monitors,
    loading,
    error,
    total,
    listMonitors,
    getMonitor,
    createMonitor,
    updateMonitor,
    deleteMonitor,
    pauseMonitor,
    resumeMonitor,
    triggerRun,
  };
};
